import { useInfiniteQuery } from '@tanstack/react-query';

import { getNextPageParam } from '@/lib/constants/pagination';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import { formatDateToISO, normalizeToStartOfDayUTC } from '@/utils/date-formatters';
import { normalizeToEndOfDayUTC } from '@/utils/date-formatters/date-formatters';

import { getTimeTracks } from '../api/time-tracking.api';
import { TIME_TRACKING_QUERY_KEYS } from '../constants/time-tracking-query-keys';
import { TimeTrack, ViewMode } from '../types/time-tracking';
import { getDateRangeForView } from './use-view-mode-and-date';

interface UseTimeTracksParams {
  date: Date;
  viewMode: ViewMode;
  projectId: number;
  enabled: boolean;
}

export const useTimeTracks = ({ date, viewMode, projectId, enabled }: UseTimeTracksParams) => {
  const { start, end } = getDateRangeForView(date, viewMode);
  const startDate = formatDateToISO(normalizeToStartOfDayUTC(start));
  const endDate = formatDateToISO(normalizeToEndOfDayUTC(end));

  return useInfiniteQuery({
    queryKey: TIME_TRACKING_QUERY_KEYS.timeTracks(startDate, endDate, projectId),
    queryFn: ({ pageParam }) =>
      getTimeTracks({
        startDate,
        endDate,
        projectId,
        cursor: pageParam,
      }),
    getNextPageParam,
    initialPageParam: undefined,
    staleTime: 1000 * 60 * 5,
    enabled,
    select: data => {
      const allItems = data.pages.flatMap(page => page.items);
      const uniqueItems = allItems.filter(
        (item, index, self) => index === self.findIndex(t => t.id === item.id)
      );
      return new PaginatedResponse<TimeTrack>({
        items: uniqueItems,
        pagination: data.pages[data.pages.length - 1]?.pagination,
      });
    },
  });
};
